/**
 * Проигрывание сохранённого React Flow (nodes + edges) вне редактора.
 */

import { isDialogueEmotionDebugEnabled } from './dialogueEmotionAggregates';

export function getOutgoingEdges(edges, sourceId) {
  return (edges || []).filter((e) => e.source === sourceId);
}

export function pickDefaultTarget(outEdges) {
  if (!outEdges.length) return null;
  const def = outEdges.find((e) => !e.sourceHandle || e.sourceHandle === 'default');
  return (def || outEdges[0]).target;
}

/** Set of node ids (string-normalized) for graph checks. */
function nodeIdSet(nodes) {
  return new Set((nodes || []).map((n) => String(n.id)));
}

/**
 * Как pickDefaultTarget, но пропускает рёбра в удалённые ноды (часто после дублирования default от Start).
 */
export function pickFirstExistingTarget(outEdges, nodes) {
  const idSet = nodeIdSet(nodes);
  if (!outEdges?.length) return null;
  const has = (tid) => tid != null && idSet.has(String(tid));
  const defaults = outEdges.filter((e) => !e.sourceHandle || e.sourceHandle === 'default');
  const scan = (arr) => {
    for (const e of arr) {
      if (has(e.target)) return e.target;
    }
    return null;
  };
  return scan(defaults.length ? defaults : outEdges) || scan(outEdges);
}

export function getFirstNodeAfterStart(nodes, edges) {
  const list = nodes || [];
  const eds = edges || [];
  const start = list.find((n) => n.type === 'start');
  if (start) {
    const outs = getOutgoingEdges(eds, start.id);
    const t = pickFirstExistingTarget(outs, list);
    if (t) return t;
  }
  const targets = new Set(eds.map((e) => e.target));
  const entries = list.filter((n) => !targets.has(n.id));
  if (entries.length === 1) return entries[0].id;
  if (entries.length > 1) {
    entries.sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0));
    return entries[0].id;
  }
  return list[0]?.id ?? null;
}

export function dialogueLine(node, lang) {
  const d = node?.data || {};
  if (lang === 'en') return (d.enText || d.text || '').trim() || '…';
  return (d.text || '').trim() || '…';
}

export function dialogueAnswerLabels(node, lang) {
  const answers = node?.data?.answers || [];
  return answers.map((a) => {
    if (lang === 'en') return (a.enText || a.text || '').trim() || '…';
    return (a.text || '').trim() || '…';
  });
}

/**
 * Цель по sourceHandle. Если передан `nodes`, цель должна существовать в графе; иначе
 * берётся первый существующий target среди исходящих (как у Start с дублями default).
 * Для явного handle (например answer-0): перебираются **все** рёбра с этим handle — старое «битое»
 * не заставляет уйти на target другого ответа.
 * @param {string|null|undefined} handleId — пусто = линейный выход (default / любой).
 */
export function getEdgeTargetByHandle(edges, sourceId, handleId, nodes) {
  const outs = getOutgoingEdges(edges || [], sourceId);
  if (nodes == null) {
    if (!handleId) return pickDefaultTarget(outs);
    const hit = outs.find((e) => e.sourceHandle === handleId);
    return hit ? hit.target : null;
  }
  const idSet = nodeIdSet(nodes);
  const has = (tid) => tid != null && idSet.has(String(tid));
  if (!handleId) return pickFirstExistingTarget(outs, nodes);
  const matches = outs.filter((e) => e.sourceHandle === handleId);
  for (const e of matches) {
    if (has(e.target)) return e.target;
  }
  return null;
}

/**
 * Явный выход по handle без подстановки «первого ребра».
 * Для Switch (none / other / item-N) fallback на первый исходящий ломал сценарий:
 * неподключённый «none» превращался в цель как у item-0 → закрытие окна вело в наградную ветку.
 */
export function getEdgeTargetByHandleStrict(edges, sourceId, handleId, nodes) {
  if (!handleId) return getEdgeTargetByHandle(edges, sourceId, handleId, nodes);
  const outs = getOutgoingEdges(edges || [], sourceId);
  if (nodes == null) {
    const hit = outs.find((e) => e.sourceHandle === handleId);
    return hit ? hit.target : null;
  }
  const idSet = nodeIdSet(nodes);
  const has = (tid) => tid != null && idSet.has(String(tid));
  const matches = outs.filter((e) => e.sourceHandle === handleId);
  for (const e of matches) {
    if (has(e.target)) return e.target;
  }
  return null;
}

/** Доп. стоимость в токенах, если из ответа ребро ведёт в Premium node. */
export function getPremiumAddonForAnswer(nodes, edges, dialogueNodeId, answerIndex) {
  const outs = getOutgoingEdges(edges || [], dialogueNodeId);
  const h = `answer-${answerIndex}`;
  const idSet = nodeIdSet(nodes);
  const has = (tid) => tid != null && idSet.has(String(tid));
  for (const e of outs) {
    if (e.sourceHandle !== h || !has(e.target)) continue;
    const target = (nodes || []).find((n) => n.id === e.target);
    if (target?.type === 'premium') return Number(target.data?.value) || 0;
  }
  return 0;
}

/** Background node: slide = скрыть Live2D персонажа на фоне. */
export function backgroundHidesLive2d(node) {
  return node?.type === 'background' && node?.data?.backgroundAssetKind === 'slide';
}

export function resolveBranchTierHandle(tierIndex) {
  const names = ['neutral', 'trusting', 'obedient', 'devoted'];
  return names[Math.max(0, Math.min(3, tierIndex))] || 'neutral';
}

export function resolveSessionNumberHandle(sessionNumber) {
  const sn = Math.max(0, Number(sessionNumber) || 0);
  if (sn <= 1) return '1';
  if (sn === 2) return '2';
  if (sn === 3) return '3';
  if (sn === 4) return '4';
  if (sn === 5) return '5';
  return 'more';
}

export function resolveRandomHandle() {
  return Math.random() < 0.5 ? 'A' : 'B';
}

/**
 * Авто-ноды после монтирования: куда идти и побочные эффекты.
 * @param {object} ctx — { hasCharacter?, sessionNumber?, relationshipTier? }
 */
export function getAutoAdvancePlan(nodes, edges, nodeId, ctx = {}) {
  const list = nodes || [];
  const eds = edges || [];
  const node = list.find((n) => n.id === nodeId);
  if (!node) return { kind: 'stop', nextId: null };
  const outs = getOutgoingEdges(eds, nodeId);

  const pick = (h) => getEdgeTargetByHandle(eds, nodeId, h, list);

  switch (node.type) {
    case 'start':
    case 'chapter':
      return { kind: 'immediate', nextId: pickFirstExistingTarget(outs, list) };
    case 'background': {
      const url = backgroundImageUrl(node);
      const assetKind = node.data?.backgroundAssetKind === 'slide' ? 'slide' : 'background';
      return {
        kind: 'background',
        url,
        assetKind,
        hideLive2d: assetKind === 'slide',
        nextId: pickFirstExistingTarget(outs, list)
      };
    }
    case 'premium':
      return { kind: 'immediate', nextId: pick('true') };
    case 'relationship': {
      const delta = Number(node.data?.value) || 0;
      if (!ctx.hasCharacter) {
        return { kind: 'immediate', nextId: pick('true') };
      }
      return { kind: 'relationship', delta, nextId: pick('true') };
    }
    case 'kink': {
      const kinkId = node.data?.kinkId != null ? String(node.data.kinkId) : '';
      return { kind: 'kink', kinkId, nextId: pick('true') };
    }
    case 'trigger': {
      const info = {
        triggerId: node.data?.triggerId,
        triggerKey: node.data?.triggerKey,
        triggerName: node.data?.triggerName
      };
      return { kind: 'trigger', info, nextId: pick('true') };
    }
    case 'skin': {
      const skinType = String(node.data?.value || 'AnalPlug');
      return {
        kind: 'skin_shop',
        skinType,
        nextBought: getEdgeTargetByHandleStrict(eds, nodeId, 'bought', list),
        nextDeclined: getEdgeTargetByHandleStrict(eds, nodeId, 'declined', list)
      };
    }
    case 'switch': {
      const items = node.data?.items || [];
      const byIndex = (i) => getEdgeTargetByHandleStrict(eds, nodeId, `item-${i}`, list);
      return {
        kind: 'gift_shop',
        items,
        nextNone: getEdgeTargetByHandleStrict(eds, nodeId, 'none', list),
        nextOther: getEdgeTargetByHandleStrict(eds, nodeId, 'other', list),
        nextByItemIndex: byIndex
      };
    }
    case 'sessionNumber': {
      if (!ctx.hasCharacter) {
        return { kind: 'immediate', nextId: pickFirstExistingTarget(outs, list) };
      }
      const h = resolveSessionNumberHandle(ctx.sessionNumber);
      return { kind: 'immediate', nextId: pick(h) };
    }
    case 'random':
      return { kind: 'immediate', nextId: pick(resolveRandomHandle()) };
    case 'branch': {
      if (!ctx.hasCharacter) {
        return {
          kind: 'immediate',
          nextId: getEdgeTargetByHandleStrict(eds, nodeId, 'neutral', list)
        };
      }
      const h = resolveBranchTierHandle(ctx.relationshipTier ?? 0);
      return {
        kind: 'immediate',
        nextId: getEdgeTargetByHandleStrict(eds, nodeId, h, list)
      };
    }
    default:
      return { kind: 'immediate', nextId: pickFirstExistingTarget(outs, list) };
  }
}

export function nextNodeId(nodes, edges, fromId, answerIndex) {
  if (!fromId) return null;
  const node = (nodes || []).find((n) => n.id === fromId);
  const outs = getOutgoingEdges(edges || [], fromId);
  if (!outs.length) return null;

  if (node?.type === 'dialogue') {
    const hasAnswers = (node.data?.answers || []).length > 0;
    if (hasAnswers && answerIndex != null) {
      return getEdgeTargetByHandle(edges, fromId, `answer-${answerIndex}`, nodes);
    }
    return (
      getEdgeTargetByHandle(edges, fromId, null, nodes) ??
      pickFirstExistingTarget(outs, nodes) ??
      pickDefaultTarget(outs)
    );
  }

  return pickFirstExistingTarget(outs, nodes) ?? pickDefaultTarget(outs);
}

/**
 * Ближайшая нода dialogue выше по графу (обратный обход рёбер от fromId).
 * Нужна для Switch (gift): выход «none» часто не подключён — возвращаем игрока к выбору ответа.
 */
export function findUpstreamDialogueNodeId(nodes, edges, fromId) {
  if (fromId == null || fromId === '') return null;
  const list = nodes || [];
  const eds = edges || [];
  const byId = new Map(list.map((n) => [String(n.id), n]));
  const start = String(fromId);
  const seen = new Set();
  let frontier = eds.filter((e) => String(e.target) === start).map((e) => String(e.source));
  while (frontier.length) {
    const nextFrontier = [];
    for (const id of frontier) {
      if (seen.has(id)) continue;
      seen.add(id);
      const n = byId.get(id);
      if (n?.type === 'dialogue') return id;
      for (const e of eds) {
        if (String(e.target) === id) {
          const src = String(e.source);
          if (!seen.has(src)) nextFrontier.push(src);
        }
      }
    }
    frontier = nextFrontier;
  }
  return null;
}

export function backgroundImageUrl(node) {
  const d = node?.data || {};
  // Полный кадр для фона/слайда; превью квадратное (R2 _preview) — только fallback
  return d.backgroundImageUrl || d.backgroundThumbnailUrl || null;
}

/** После выбора ответа: пропустить цепочку Premium нод (токены уже списаны в UI). */
export function skipAfterPremiumChain(nodes, edges, fromId) {
  let id = fromId;
  const list = nodes || [];
  const eds = edges || [];
  for (;;) {
    if (!id) return null;
    const cur = id;
    const node = list.find((n) => n.id === cur);
    if (!node || node.type !== 'premium') return cur;
    const outs = getOutgoingEdges(eds, cur);
    const trueOuts = outs.filter((e) => e.sourceHandle === 'true');
    const nextId =
      pickFirstExistingTarget(trueOuts.length ? trueOuts : outs, list) ??
      pickFirstExistingTarget(outs, list);
    id = nextId;
    if (id == null) return cur;
  }
}

/**
 * Дополняет поля триггера по каталогу `/api/triggers` (в JSON флоу key не обязан храниться).
 * Порядок: `triggerId` → `triggerKey` → `triggerName`; если ничего нет — `emotionKey` (например эмоция).
 * @param {object|null|undefined} info — node.data или plan.info
 * @param {object[]|undefined} catalog — строки { _id, key, name }
 * @returns {{ triggerId: string, triggerKey: string, triggerName: string }}
 */
export function resolveTriggerFromCatalog(info, catalog) {
  const list = Array.isArray(catalog) ? catalog : [];
  const s = (v) => (v != null && v !== '' ? String(v).trim() : '');

  let triggerId = s(info?.triggerId);
  let triggerKey = s(info?.triggerKey);
  let triggerName = s(info?.triggerName);
  const emotionKey = s(info?.emotionKey);

  const byId = (id) => list.find((t) => String(t._id) === id);
  const byKey = (k) => list.find((t) => String(t.key) === k);

  if (triggerId) {
    const row = byId(triggerId);
    if (row) {
      if (!triggerKey) triggerKey = s(row.key);
      if (!triggerName) triggerName = s(row.name);
    }
  } else if (triggerKey) {
    const row = byKey(triggerKey);
    if (row) {
      triggerId = s(row._id);
      if (!triggerName) triggerName = s(row.name);
    }
  } else if (triggerName) {
    const row = list.find((t) => s(t.name) === triggerName);
    if (row) {
      triggerId = s(row._id);
      triggerKey = s(row.key);
    }
  } else if (emotionKey) {
    triggerKey = emotionKey;
    const row = byKey(emotionKey);
    if (row) {
      triggerId = s(row._id);
      if (!triggerName) triggerName = s(row.name);
    }
  }

  return { triggerId, triggerKey, triggerName };
}

// --- Live2D anim graph (VelvetLive2dAnimController.animGraph in scene character) ---

/** @typedef {{ nodes?: object[], edges?: object[] }} AnimGraphSnapshot */

function animNodeIdSet(nodes) {
  return new Set((nodes || []).map((n) => String(n.id)));
}

/**
 * Стартовая нода персонажа: первая `live2dAnim` с modelPath по порядку слева направо (position.x, y).
 * @param {object[]|undefined} nodes
 * @returns {string|null}
 */
export function getEntryLive2dAnimNodeId(nodes) {
  const list = (nodes || []).filter(
    (n) => n.type === 'live2dAnim' && String(n.data?.modelPath || '').trim()
  );
  if (!list.length) return null;
  list.sort(
    (a, b) =>
      (a.position?.x ?? 0) - (b.position?.x ?? 0) || (a.position?.y ?? 0) - (b.position?.y ?? 0)
  );
  return list[0].id != null ? String(list[0].id) : null;
}

/**
 * Для отладки: как выбрана стартовая нода (сортировка по position.x, затем y среди live2dAnim с modelPath).
 * @param {object[]|undefined} nodes
 */
export function describeLive2dAnimEntryNode(nodes) {
  const all = nodes || [];
  const byType = all.reduce((acc, n) => {
    const t = n.type || '(no type)';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const live2dWithPath = all.filter(
    (n) => n.type === 'live2dAnim' && String(n.data?.modelPath || '').trim()
  );
  const live2dNoPath = all.filter(
    (n) => n.type === 'live2dAnim' && !String(n.data?.modelPath || '').trim()
  );

  const sorted = [...live2dWithPath].sort(
    (a, b) =>
      (a.position?.x ?? 0) - (b.position?.x ?? 0) || (a.position?.y ?? 0) - (b.position?.y ?? 0)
  );
  const entry = sorted[0];
  const entryNodeId = entry?.id != null ? String(entry.id) : null;

  const candidatesOrdered = sorted.map((n) => ({
    id: String(n.id),
    modelPath: n.data?.modelPath,
    motionGroup: n.data?.motionGroup,
    motionIndex: n.data?.motionIndex,
    label: n.data?.label || '',
    x: n.position?.x ?? 0,
    y: n.position?.y ?? 0,
    isEntry: Boolean(entryNodeId && String(n.id) === entryNodeId)
  }));

  return {
    rule: 'live2dAnim + non-empty data.modelPath, sort by position.x then y, first wins',
    entryNodeId,
    nodeTypeCounts: byType,
    totalNodesInGraph: all.length,
    live2dAnimWithModelPathCount: live2dWithPath.length,
    live2dAnimMissingModelPathIds: live2dNoPath.map((n) => String(n.id)),
    candidatesOrdered
  };
}

export function findLive2dAnimNodeById(nodes, nodeId) {
  if (nodeId == null || nodeId === '') return null;
  const s = String(nodeId);
  return (nodes || []).find((n) => String(n.id) === s) || null;
}

export function getAnimOutgoingEdges(edges, sourceId) {
  const sid = String(sourceId);
  return (edges || []).filter((e) => String(e.source) === sid);
}

/**
 * Цель по sourceHandle; target должен существовать среди нод графа.
 * @param {object[]|undefined} edges
 * @param {string} sourceId
 * @param {string} sourceHandle
 * @param {object[]|undefined} animNodes
 */
export function getAnimEdgeTargetStrict(edges, sourceId, sourceHandle, animNodes) {
  if (!sourceHandle) return null;
  const idSet = animNodeIdSet(animNodes);
  const hit = getAnimOutgoingEdges(edges, sourceId).find((e) => e.sourceHandle === sourceHandle);
  if (!hit?.target) return null;
  const tid = String(hit.target);
  return idSet.has(tid) ? tid : null;
}

/** Есть ли у live2dAnim-ноды исходящее onComplete (`done`) в валидную ноду. */
export function live2dAnimNodeHasDoneOut(animGraph, nodeId) {
  if (!animGraph?.nodes?.length || nodeId == null || nodeId === '') return false;
  return Boolean(
    getAnimEdgeTargetStrict(animGraph.edges || [], nodeId, 'done', animGraph.nodes)
  );
}

export function live2dAnimNodeHasTriggerId(node, triggerIdStr) {
  if (!node || node.type !== 'live2dAnim') return false;
  const tid = String(triggerIdStr || '').trim();
  if (!tid) return false;
  const ids = node.data?.triggerIds || [];
  return ids.some((x) => String(x) === tid);
}

function handleForFlowTriggerId(triggerIdStr) {
  const tid = String(triggerIdStr || '').trim();
  return tid ? `t:${tid}` : '';
}

/**
 * Нода с триггером `t:id` на текущей позиции или дальше строго по цепочке onComplete (`done`).
 * Нужна чтобы story-триггер сразу дал кроссфейд, а не зависал в loop до motionFinish.
 */
export function resolveAnimTriggerTargetAlongDoneChain(animGraph, startNodeId, triggerIdFromFlow) {
  if (!animGraph?.nodes?.length || startNodeId == null || startNodeId === '') return null;
  const tid = String(triggerIdFromFlow || '').trim();
  const h = handleForFlowTriggerId(tid);
  if (!h) return null;
  const nodes = animGraph.nodes;
  const edges = animGraph.edges || [];
  const visited = new Set();
  let id = String(startNodeId);
  while (id && !visited.has(id)) {
    visited.add(id);
    const node = findLive2dAnimNodeById(nodes, id);
    if (node && live2dAnimNodeHasTriggerId(node, tid)) {
      return getAnimEdgeTargetStrict(edges, id, h, nodes);
    }
    const doneTarget = getAnimEdgeTargetStrict(edges, id, 'done', nodes);
    if (!doneTarget) return null;
    id = doneTarget;
  }
  return null;
}

/**
 * Снять с очереди подходящие триггеры, пока на текущей ноде есть совпадение и ребро.
 * @param {AnimGraphSnapshot} animGraph
 * @param {string} landNodeId
 * @param {{ triggerId: string }[]} pending
 */
export function consumePendingAnimTriggers(animGraph, landNodeId, pending) {
  const nodes = animGraph?.nodes || [];
  const edges = animGraph?.edges || [];
  let nodeId = String(landNodeId);
  let queue = [...pending];
  for (;;) {
    const node = findLive2dAnimNodeById(nodes, nodeId);
    if (!node) return { nodeId, pending: queue };
    const idx = queue.findIndex((t) => live2dAnimNodeHasTriggerId(node, t.triggerId));
    if (idx < 0) return { nodeId, pending: queue };
    const tid = String(queue[idx].triggerId || '').trim();
    queue = queue.filter((_, i) => i !== idx);
    const h = handleForFlowTriggerId(tid);
    const next = h ? getAnimEdgeTargetStrict(edges, nodeId, h, nodes) : null;
    if (!next) return { nodeId, pending: queue };
    nodeId = next;
  }
}

/**
 * @param {AnimGraphSnapshot|null|undefined} animGraph
 * @param {{ nodeId: string|null, pending: { triggerId: string }[] }} state
 * @param {string} triggerIdFromFlow — Mongo id триггера из ноды Trigger (story flow)
 */
export function reduceAnimGraphOnFlowTrigger(animGraph, state, triggerIdFromFlow) {
  if (!animGraph?.nodes?.length || !state.nodeId) return state;
  const tid = String(triggerIdFromFlow || '').trim();
  if (!tid) return state;
  const next = resolveAnimTriggerTargetAlongDoneChain(animGraph, state.nodeId, tid);
  if (next) {
    const landed = consumePendingAnimTriggers(animGraph, next, state.pending);
    return { nodeId: landed.nodeId, pending: landed.pending };
  }
  return { ...state, pending: [...state.pending, { triggerId: tid }] };
}

/**
 * Окончание проигрывания текущего клипа (Cubism motionFinish — для зацикленных клипов не вызывается).
 * @param {AnimGraphSnapshot|null|undefined} animGraph
 * @param {{ nodeId: string|null, pending: { triggerId: string }[] }} state
 */
export function reduceAnimGraphOnMotionComplete(animGraph, state) {
  if (!animGraph?.nodes?.length || !state.nodeId) return state;
  const nodes = animGraph.nodes;
  const edges = animGraph.edges || [];
  const doneTarget = getAnimEdgeTargetStrict(edges, state.nodeId, 'done', nodes);
  if (!doneTarget) return state;
  const landed = consumePendingAnimTriggers(animGraph, doneTarget, state.pending);
  return { nodeId: landed.nodeId, pending: landed.pending };
}

export function live2dAnimNodeHasAggregateEmotionKey(node, aggregateKeyStr) {
  if (!node || node.type !== 'live2dAnim') return false;
  const k = String(aggregateKeyStr || '').trim();
  if (!k) return false;
  const emotions = node.data?.emotions || [];
  return emotions.some((x) => String(x) === k);
}

function handleForAggregateEmotionKey(keyStr) {
  const k = String(keyStr || '').trim();
  return k ? `e:${k}` : '';
}

/**
 * Обход цепочки done + поиск эмоции `e:Key`. Возвращает цель и трассу для консоли.
 * @returns {{ next: string|null, trace: object[], stoppedReason: string }}
 */
export function traceAnimAggregateEmotionResolve(animGraph, startNodeId, aggregateKey) {
  const trace = [];
  if (!animGraph?.nodes?.length || startNodeId == null || startNodeId === '') {
    return { next: null, trace, stoppedReason: 'no_graph_or_start' };
  }
  const k = String(aggregateKey || '').trim();
  const h = handleForAggregateEmotionKey(k);
  if (!h) return { next: null, trace, stoppedReason: 'empty_key' };
  const nodes = animGraph.nodes;
  const edges = animGraph.edges || [];
  const visited = new Set();
  let id = String(startNodeId);
  let step = 0;
  while (id && !visited.has(id)) {
    visited.add(id);
    const node = findLive2dAnimNodeById(nodes, id);
    const emotions = node?.type === 'live2dAnim' ? [...(node.data?.emotions || [])] : [];
    const emotionMatch = Boolean(node && live2dAnimNodeHasAggregateEmotionKey(node, k));
    const edgeE = emotionMatch ? getAnimEdgeTargetStrict(edges, id, h, nodes) : null;
    const edgeDone = getAnimEdgeTargetStrict(edges, id, 'done', nodes);
    trace.push({
      step: step++,
      nodeId: id,
      reactFlowType: node?.type ?? '(missing)',
      clipLabel: node?.data?.label ?? '',
      motion: node?.type === 'live2dAnim' ? `${node.data?.motionGroup || 'idle'}#${node.data?.motionIndex ?? 0}` : '',
      emotions,
      emotionKeyWanted: k,
      emotionMatched: emotionMatch,
      edgeHandle: h,
      edgeEmotionTarget: edgeE,
      edgeDoneTarget: edgeDone
    });
    if (emotionMatch) {
      if (edgeE) return { next: edgeE, trace, stoppedReason: 'emotion_edge_found' };
      return { next: null, trace, stoppedReason: 'emotion_on_node_but_no_e_edge' };
    }
    if (!edgeDone) {
      return { next: null, trace, stoppedReason: 'done_chain_broken' };
    }
    id = edgeDone;
  }
  return { next: null, trace, stoppedReason: 'cycle_or_empty' };
}

/**
 * Нода с эмоцией `e:Key` на текущей позиции или дальше по цепочке onComplete (`done`).
 */
export function resolveAnimAggregateEmotionAlongDoneChain(animGraph, startNodeId, aggregateKey) {
  return traceAnimAggregateEmotionResolve(animGraph, startNodeId, aggregateKey).next;
}

/**
 * Реакция на агрегатную эмоцию из текста диалога (*shy* → Blush и т.д.). Без очереди: нет ребра — состояние не меняем.
 */
export function reduceAnimGraphOnDialogueAggregateEmotion(animGraph, state, aggregateKey) {
  if (!animGraph?.nodes?.length || !state.nodeId) {
    console.info('[DialogueEmotion] animReducer skip', {
      reason: 'no_graph_or_current_anim_node',
      hasGraph: Boolean(animGraph?.nodes?.length),
      currentAnimNodeId: state.nodeId
    });
    return state;
  }
  const k = String(aggregateKey || '').trim();
  if (!k) return state;

  const { next, trace, stoppedReason } = traceAnimAggregateEmotionResolve(animGraph, state.nodeId, k);
  console.info('[DialogueEmotion] animGraph.resolve', {
    aggregateKey: k,
    handle: `e:${k}`,
    currentAnimNodeId: state.nodeId,
    stoppedReason,
    immediateTarget: next,
    steps: trace
  });

  if (!next) {
    console.warn(
      `[DialogueEmotion] Нет перехода e:${k} — см. animGraph.resolve.steps выше (нужны эмоция «${k}» на live2dAnim-ноде + исходящее ребро с handle e:${k}).`
    );
    return state;
  }

  const landed = consumePendingAnimTriggers(animGraph, next, state.pending);
  const targetNode = findLive2dAnimNodeById(animGraph.nodes, landed.nodeId);
  console.info('[DialogueEmotion] animState.updated', {
    aggregateKey: k,
    fromAnimNodeId: state.nodeId,
    crossedToNodeId: next,
    landedAnimNodeId: landed.nodeId,
    landedClip: targetNode?.type === 'live2dAnim'
      ? {
          label: targetNode.data?.label,
          motionGroup: targetNode.data?.motionGroup || 'idle',
          motionIndex: targetNode.data?.motionIndex ?? 0,
          modelPath: targetNode.data?.modelPath
        }
      : null,
    pendingTriggersAfter: (landed.pending || []).length
  });

  if (isDialogueEmotionDebugEnabled()) {
    console.info('[DialogueEmotion] animReducer pending queue', landed.pending);
  }

  return { nodeId: landed.nodeId, pending: landed.pending };
}
